import { useState, useEffect } from "react";
import { Menu, X, Globe } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { LogoWithZoom } from "@/components/ImageWithZoom";

export const Navigation = ({ isScrolled: externalIsScrolled }) => {
  const [isScrolled, setIsScrolled] = useState(externalIsScrolled || false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    if (externalIsScrolled !== undefined) return;
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [externalIsScrolled]);

  const navLinks = [
    { href: "/#villas", label: t("nav.villas") },
    { href: "/#adventures", label: t("nav.adventures") },
    { href: "/#amenities", label: t("nav.amenities") },
    { href: "/#reviews", label: t("nav.reviews") },
    { href: "/#location", label: t("nav.location") },
  ];

  return (
    <>
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-primary focus:text-white focus:px-4 focus:py-2 focus:rounded-md focus:outline-none focus:ring-2 focus:ring-white"
        data-testid="skip-nav"
      >
        Skip to main content
      </a>
      
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || externalIsScrolled ? "nav-glass nav-scrolled py-3" : "bg-transparent py-6"
        }`}
        data-testid="main-navigation"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-lg" data-testid="logo-link" aria-label="Palmaréca Selah Village - Home">
            <LogoWithZoom isScrolled={isScrolled || externalIsScrolled} size="normal" />
          </a>

          <div className="hidden md:flex items-center gap-8" role="menubar">
            {navLinks.map((link, i) => (
              <a
                key={i}
                href={link.href}
                className={`font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md px-2 py-1 ${
                  isScrolled || externalIsScrolled
                    ? "text-foreground/80 hover:text-primary" 
                    : "text-white/90 hover:text-white"
                }`}
                data-testid={`nav-link-${i}`}
                role="menuitem"
              >
                {link.label}
              </a>
            ))}
          
          <button
            onClick={toggleLanguage}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full border transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
              isScrolled || externalIsScrolled
                ? "border-primary/20 text-primary hover:bg-primary/10"
                : "border-white/30 text-white hover:bg-white/10"
            }`}
            data-testid="language-toggle"
            aria-label={`Switch language to ${language === 'en' ? 'Spanish' : 'English'}`}
            role="button"
          >
            <Globe size={16} aria-hidden="true" />
            <span className="text-sm font-medium">{language.toUpperCase()}</span>
          </button>
          
          <a 
            href="/#villas"
            className={`btn-accent inline-flex items-center gap-2 text-sm px-6 py-3 transition-transform duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2`}
            data-testid="nav-book-now"
            role="button"
          >
            {t("nav.book")}
          </a>
        </div>

        <button
          className="md:hidden p-2 focus:outline-none focus:ring-2 focus:ring-primary rounded-md"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {isMobileMenuOpen ? (
            <X className={isScrolled || externalIsScrolled ? "text-primary" : "text-white"} size={24} />
          ) : (
            <Menu className={isScrolled || externalIsScrolled ? "text-primary" : "text-white"} size={24} aria-hidden="true" />
          )}
        </button>
      </div>

      {isMobileMenuOpen && (
        <div 
          className="md:hidden absolute top-full left-0 right-0 bg-white shadow-lg py-6 px-6"
          role="menu"
          aria-label="Mobile navigation menu"
        >
          <div className="flex flex-col gap-4">
            {navLinks.map((link, i) => (
              <a
                key={i}
                href={link.href}
                className="text-foreground/80 hover:text-primary font-medium py-2 focus:outline-none focus:ring-2 focus:ring-primary rounded-md px-2"
                onClick={() => setIsMobileMenuOpen(false)}
                role="menuitem"
              >
                {link.label}
              </a>
            ))}
            
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 py-2 text-foreground/80 hover:text-primary font-medium focus:outline-none focus:ring-2 focus:ring-primary rounded-md px-2"
              aria-label={`Switch language to ${language === 'en' ? 'Spanish' : 'English'}`}
            >
              <Globe size={18} aria-hidden="true" />
              {language === "en" ? "Español" : "English"}
            </button>
            
            <a 
              href="/#villas"
              className="btn-accent text-center mt-4 focus:outline-none focus:ring-2 focus:ring-white"
              onClick={() => setIsMobileMenuOpen(false)}
              role="menuitem"
            >
              {t("nav.book")}
            </a>
          </div>
        </div>
      )}
    </nav>
    </>
  );
};
